import { useQueryClient } from "@tanstack/react-query"
import { Form, Formik, FormikValues } from "formik"
import * as Yup from "yup"
import round from "lodash/round"
import { DateTime } from "luxon"

import { Toast } from "../../components/Toast"
import WizardHeader from "../../components/WizardHeader"
import LiabilityInsuranceFinalizeForm from "./LiabilityInsuranceFinalizeForm"
import WizardFooter from "../../components/WizardFooter"
import { SubmitButton } from "../../components/SubmitButton"
import { requiresLegalEntityName } from "../PropertyDetails/PropertyDetailsFormHelpers"
import {
  useAccountId,
  useCreateStripeSession,
  useUpdateInsuranceWizard,
} from "../../hooks"
import { genericErrMsg } from "../../api/auth"
import { LiabilityInsuranceTypes } from "@/types/liabilityInsuranceWizard"
import { AccountTypes } from "@/types/account"
import { Profile } from "@/types"

export interface LiabilityInsuranceFinalizeProps {
  handlePrevious: () => void
  insuranceWizardData: LiabilityInsuranceTypes
  account: AccountTypes
  profile: Profile | undefined
}

const LiabilityInsuranceFinalize = ({
  handlePrevious,
  insuranceWizardData,
  account,
  profile,
}: LiabilityInsuranceFinalizeProps) => {
  const queryClient = useQueryClient()
  const accountId = useAccountId()

  const acreage = round(insuranceWizardData?.acreage)

  const generalPremium = insuranceWizardData?.general_liability_price_dollars

  const MembershipFee = account?.is_member ? 0 : 12

  const totalPremium = generalPremium + MembershipFee

  const legalEntityNameRequired = requiresLegalEntityName(
    account?.ownership_type
  )

  const legalEntityNameLabel = legalEntityNameRequired
    ? "Legal entity name"
    : "Legal entity name (if applicable)"

  const coveragePeriodOptions =
    insuranceWizardData.available_coverage_periods.map((period) => ({
      value: `${period.start_date}||${period.end_date}`,
      label: period.display_name,
    }))

  const validationSchema = Yup.object().shape({
    coverage_period: Yup.string().required("Coverage period is required"),
    full_name: Yup.string().required("Full name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    property_address_line_1: Yup.string().required(
      "Property address is required"
    ),
    property_city: Yup.string().required("Property city is required"),
    property_state: Yup.string().required("Property state is required"),
    property_zip_code: Yup.string().required("Property zip code is required"),
    phone_number: Yup.string().required("Phone number is required"),
    legal_entity_name: legalEntityNameRequired
      ? Yup.string().required("Legal entity name is required")
      : Yup.string().notRequired(),
    electronic_signature: Yup.string().required(
      "Digital signature is required"
    ),
    electronic_signature_date: Yup.date()
      .required("Date is required")
      .min(
        new Date(new Date().setHours(0, 0, 0, 0)),
        "Date cannot be in the past"
      ),
  })

  const {
    mutateAsync: createStripeSession,
    isPending: isCreatingStripeSession,
  } = useCreateStripeSession(queryClient, accountId, {
    onSuccess: (data: { url: string }) => {
      window.location.href = data.url
    },
    onError: (error: { detail: string }) => {
      Toast.error(error?.detail || genericErrMsg)
    },
  })

  const { mutateAsync: updateWizard, isPending: isUpdatingWizard } =
    useUpdateInsuranceWizard(queryClient, accountId, {
      onSuccess: async () => {
        const postData = {
          lookup_key: insuranceWizardData?.selected_implementation,
          redirect_path: "liability-insurance",
        }

        await createStripeSession(postData)
      },
      onError: (error: { detail: string }) => {
        Toast.error(error?.detail || genericErrMsg)
      },
    })

  const handleSubmit = async (values: FormikValues) => {
    const [start_date, end_date] = values.coverage_period.split("||")

    await updateWizard({
      wizard_key: "insurance",
      finalization_data: {
        property_address_line_1: values.property_address_line_1,
        property_address_line_2: values.property_address_line_2,
        property_city: values.property_city,
        property_state: values.property_state,
        property_zip_code: values.property_zip_code,
        legal_entity_name: values.legal_entity_name,
        phone_number: values.phone_number,
        electronic_signature: values.electronic_signature,
        electronic_signature_date: values.electronic_signature_date,
        coverage_period: {
          start_date,
          end_date,
        },
      },
    })
  }

  return (
    <div
      className="pt-10 lg:pt-13 pb-31"
      data-testid="liability-insurance-finalize"
    >
      <WizardHeader
        eyebrow="Your NCX Guide"
        heading="Confirm your info and complete your purchase"
        text="Payment will bind coverage and a certificate will be issued to you via email in 2-3 business days."
      />

      <Formik
        initialValues={{
          full_name: account?.account_name || "",
          email: profile?.email || "",
          property_address_line_1:
            insuranceWizardData?.property_address_line_1 ||
            account?.property_address_line_1 ||
            "",
          property_address_line_2:
            insuranceWizardData?.property_address_line_2 ||
            account?.property_address_line_2 ||
            "",
          property_city:
            insuranceWizardData?.property_city || account?.property_city || "",
          property_state:
            insuranceWizardData?.property_state ||
            account?.property_state ||
            "",
          property_zip_code:
            insuranceWizardData?.property_zip_code ||
            account?.property_zip_code ||
            "",
          phone_number:
            insuranceWizardData?.phone_number ||
            account?.optional_contact_phone_number ||
            "",
          legal_entity_name:
            insuranceWizardData?.legal_entity_name ||
            account?.legal_entity_name ||
            "",
          electronic_signature: insuranceWizardData?.electronic_signature || "",
          electronic_signature_date:
            insuranceWizardData?.electronic_signature_date
              ? DateTime.fromISO(
                  insuranceWizardData?.electronic_signature_date
                ).toFormat("yyyy-MM-dd")
              : DateTime.now().toFormat("yyyy-MM-dd"),
          coverage_period: insuranceWizardData?.current_coverage_period
            ? `${insuranceWizardData.current_coverage_period.start_date}||${insuranceWizardData.current_coverage_period.end_date}`
            : "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <LiabilityInsuranceFinalizeForm
              legalEntityNameLabel={legalEntityNameLabel}
              acreage={acreage}
              generalPremium={generalPremium}
              totalPremium={totalPremium}
              coveragePeriodOptions={coveragePeriodOptions}
              isMember={account?.is_member}
            />

            <WizardFooter>
              <div className="flex gap-4 ml-auto">
                <button
                  type="button"
                  className="liability-insurance-finalize-go-back-button btn2 btn2-outline-primary font-bold tracking-[0.32px] whitespace-nowrap"
                  onClick={handlePrevious}
                  data-testid="liability-insurance-finalize-go-back-button"
                >
                  Go Back
                </button>

                <SubmitButton
                  className="liability-insurance-coverage-finalize-complete-purchase btn2 btn2-primary font-bold tracking-[0.32px] whitespace-nowrap"
                  isSubmitting={
                    isSubmitting || isUpdatingWizard || isCreatingStripeSession
                  }
                  data-testid="liability-insurance-finalize-complete-purchase-button"
                >
                  Complete Purchase
                </SubmitButton>
              </div>
            </WizardFooter>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default LiabilityInsuranceFinalize
